import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { Check } from 'lucide-react'
import { cn } from '@/lib/utils'
import { AuthModalField } from '@/components/auth/AuthModalField'
import { AuthPrimaryButton } from '@/components/auth/AuthPrimaryButton'
import { SocialLoginButtons } from '@/components/auth/SocialLoginButtons'
import { useAuthModal } from '@/context/AuthModalContext'
import { useLoader } from '@/context/LoaderContext'
import { useUsernameAvailability } from '@/hooks/use-username-availability'
import { isUsernameFormatValid, registerSchema, type RegisterFormValues } from '@/lib/auth-validation'
import { clearReferralCode, getReferralCode } from '@/lib/referral-tracking'
import { useRegisterMutation } from '@/store/api/authApi'

export function RegisterForm() {
	const { setView } = useAuthModal()
	const { showLoader, hideLoader } = useLoader()
	const [registerUser, { isLoading }] = useRegisterMutation()

	useEffect(() => {
		if (!isLoading) {
			return
		}

		showLoader()
		return () => hideLoader()
	}, [isLoading, showLoader, hideLoader])

	const {
		register,
		handleSubmit,
		setValue,
		setError,
		watch,
		formState: { errors },
	} = useForm<RegisterFormValues>({
		resolver: zodResolver(registerSchema),
		defaultValues: {
			username: '',
			first_name: '',
			last_name: '',
			email: '',
			password: '',
			confirmPassword: '',
			agreedToTerms: false,
		},
	})

	const agreedToTerms = watch('agreedToTerms')
	const username = watch('username')
	const usernameAvailability = useUsernameAvailability(username)

	const onSubmit = async (values: RegisterFormValues) => {
		const trimmedUsername = values.username.trim()

		if (isUsernameFormatValid(trimmedUsername)) {
			if (usernameAvailability.isChecking || !usernameAvailability.isChecked) {
				setError('username', {
					message: 'Please wait while we check username availability',
				})
				return
			}

			if (!usernameAvailability.isAvailable) {
				setError('username', {
					message: usernameAvailability.message ?? 'This username is not available',
				})
				return
			}
		}

		const { confirmPassword: _confirmPassword, agreedToTerms: _agreed, ...payload } = values

		const referrerCode = getReferralCode()
		const body = {
			username: trimmedUsername,
			first_name: payload.first_name,
			last_name: payload.last_name,
			email: payload.email,
			password: payload.password,
			...(referrerCode ? { referrer_code: referrerCode } : {}),
		}

		try {
			await registerUser(body).unwrap()
			// A successful registration means this referral was attributed to a newly created account.
			clearReferralCode()
			setView('login')
		} catch {
			// Error toast is shown globally by the API interceptor.
		}
	}

	return (
		<form noValidate className='flex flex-col gap-4' onSubmit={handleSubmit(onSubmit)}>
			<AuthModalField
				type='text'
				placeholder='Username'
				autoComplete='username'
				error={errors.username?.message}
				helperText={usernameAvailability.message}
				helperTone={usernameAvailability.tone}
				isChecking={usernameAvailability.isChecking}
				{...register('username')}
			/>

			<div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
				<AuthModalField type='text' placeholder='First name' autoComplete='given-name' error={errors.first_name?.message} {...register('first_name')} />
				<AuthModalField type='text' placeholder='Last name' autoComplete='family-name' error={errors.last_name?.message} {...register('last_name')} />
			</div>

			<AuthModalField type='email' placeholder='Email address' autoComplete='email' error={errors.email?.message} {...register('email')} />
			<AuthModalField type='password' placeholder='Password' autoComplete='new-password' error={errors.password?.message} {...register('password')} />
			<AuthModalField type='password' placeholder='Confirm Password' autoComplete='new-password' error={errors.confirmPassword?.message} {...register('confirmPassword')} />

			<div className='space-y-1.5'>
				<label className='flex cursor-pointer items-start gap-3'>
					<button
						type='button'
						role='checkbox'
						aria-checked={agreedToTerms}
						onClick={() =>
							setValue('agreedToTerms', !agreedToTerms, {
								shouldValidate: true,
							})
						}
						className={cn(
							'mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition-colors',
							agreedToTerms ? 'border-primary bg-primary text-background' : 'border-border bg-secondary text-transparent',
						)}
					>
						<Check className='h-3.5 w-3.5' strokeWidth={3} />
					</button>
					<span className='text-sm leading-[140%] tracking-[-0.21px] text-muted-foreground'>
						I'm 18+ years old and I agree to{' '}
						<button type='button' className='text-foreground underline underline-offset-2 transition-opacity hover:opacity-80'>
							Terms &amp; Policy
						</button>{' '}
						of BetsCycle
					</span>
				</label>
				{errors.agreedToTerms?.message ? <p className='text-xs leading-[140%] tracking-[-0.18px] text-destructive'>{errors.agreedToTerms.message}</p> : null}
			</div>

			<AuthPrimaryButton label='Create account' type='submit' isLoading={isLoading} disabled={usernameAvailability.isChecking} />

			<div className='mt-3 flex items-center gap-2'>
				<span className='h-px flex-1 bg-border' />
				<span className='text-sm font-medium tracking-[-0.21px] text-subtle-foreground'>OR</span>
				<span className='h-px flex-1 bg-border' />
			</div>

			<SocialLoginButtons />
		</form>
	)
}
